import { useEffect, useState } from "react";
import { api, formatCHF, formatApiError } from "../lib/api";
import { useT } from "../i18n/I18nContext";
import { Link } from "react-router-dom";
import { Users, Gavel, Wallet, FolderKanban, PlusCircle, Inbox } from "lucide-react";
import { toast } from "sonner";

export default function AdminDashboard() {
  const t = useT();
  const [groups, setGroups] = useState([]);
  const [stats, setStats] = useState({ groups: 0, participants: 0, paidSum: 0, openAuctions: 0, newLeads: 0 });

  const load = async () => {
    try {
      const [gs, , pays, aucs, leads] = await Promise.all([
        api.get("/groups"),
        api.get("/users"),
        api.get("/payments"),
        api.get("/auctions"),
        api.get("/leads").catch(() => ({ data: [] })),
      ]);
      setGroups(gs.data);
      const paidSum = pays.data.filter((p) => ["paid", "checked", "released"].includes(p.status)).reduce((a, b) => a + (b.amount || 0), 0);
      const openAuctions = aucs.data.filter((a) => a.status === "open").length;
      const newLeads = leads.data.filter((l) => l.status === "new").length;
      const partsArr = await Promise.all(
        gs.data.map((g) => api.get(`/groups/${g.id}/participants`).then((r) => r.data).catch(() => []))
      );
      const totalParts = partsArr.reduce((acc, ps) => acc + ps.length, 0);
      setStats({ groups: gs.data.length, participants: totalParts, paidSum, openAuctions, newLeads });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-8">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="nv-label">{t("admin.title")}</div>
          <h1 className="font-heading text-3xl md:text-4xl font-medium text-[#0F4C3A] mt-2" data-testid="admin-dashboard-title">
            {t("admin.overview")}
          </h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link
            to="/admin/leads"
            data-testid="leads-link"
            className="px-5 py-2.5 rounded-md font-medium border border-[#0F4C3A] text-[#0F4C3A] hover:bg-[#F8FAF9] inline-flex items-center gap-2 relative"
          >
            <Inbox className="w-4 h-4" /> {t("nav.leads")}
            {stats.newLeads > 0 && (
              <span className="absolute -top-2 -right-2 bg-[#D4AF37] text-[#0F4C3A] text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" data-testid="leads-badge">
                {stats.newLeads > 9 ? "9+" : stats.newLeads}
              </span>
            )}
          </Link>
          <Link
            to="/admin/groups/new"
            data-testid="new-group-button"
            className="nv-btn-primary px-5 py-2.5 rounded-md font-medium inline-flex items-center gap-2"
          >
            <PlusCircle className="w-4 h-4" /> {t("admin.new_group")}
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-[#E2E8E4] rounded-md overflow-hidden mb-8">
        <Stat icon={FolderKanban} label={t("admin.stats.groups")} value={stats.groups} />
        <Stat icon={Users} label={t("admin.stats.participants")} value={stats.participants} />
        <Stat icon={Wallet} label={t("admin.stats.received")} value={formatCHF(stats.paidSum)} mono />
        <Stat icon={Gavel} label={t("admin.stats.open_auctions")} value={stats.openAuctions} highlight />
      </div>

      {/* Groups list */}
      <div className="nv-border bg-white rounded-md">
        <div className="p-5 border-b border-[#E2E8E4] flex items-center justify-between">
          <div>
            <div className="nv-label">{t("nav.groups")}</div>
            <div className="font-heading text-xl font-medium text-[#0F4C3A]">{t("admin.all_groups")}</div>
          </div>
        </div>
        {groups.length === 0 ? (
          <div className="p-10 text-center text-[#94A3B8]">{t("admin.no_groups")}</div>
        ) : (
          <div className="divide-y divide-[#E2E8E4]">
            {groups.map((g) => (
              <Link
                key={g.id}
                to={`/admin/groups/${g.id}`}
                data-testid={`group-row-${g.id}`}
                className="flex items-center justify-between px-5 py-4 hover:bg-[#F8FAF9] transition"
              >
                <div>
                  <div className="font-heading text-lg font-medium text-[#0F4C3A]">{g.name}</div>
                  <div className="text-sm text-[#475569] mt-1">
                    {g.shares} · {g.start_date} · {t("dashboard.month")} {g.current_month}
                  </div>
                </div>
                <div className="text-xs tracking-[0.2em] uppercase text-[#94A3B8]">{g.status}</div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, mono, highlight }) {
  return (
    <div className={`p-5 md:p-6 border-r border-b md:border-b-0 border-[#E2E8E4] last:border-r-0 ${highlight ? "bg-[#0F4C3A] text-white" : "bg-white"}`}>
      <div className="flex items-start justify-between">
        <div className={`nv-label ${highlight ? "!text-[#D4AF37]" : ""}`}>{label}</div>
        <Icon className={`w-4 h-4 ${highlight ? "text-[#D4AF37]" : "text-[#94A3B8]"}`} />
      </div>
      <div className={`mt-3 ${mono ? "font-mono-nv" : "font-heading"} text-2xl md:text-3xl font-semibold ${highlight ? "text-white" : "text-[#0F4C3A]"}`}>
        {value}
      </div>
    </div>
  );
}
